<template>
    <div>
        <!-- Page Banner -->
        <PageBanner 
            :pageTitle="page ? page.title : 'Page'" 
            :backgroundImage="page ? page.banner_image : ''"
        />
        
        <!-- Page Content Area -->
        <div class="about-area ptb-115" v-if="!loading && !error && page">
            <div class="container">
                <!-- Main Content Section -->
                <div class="row mb-55" v-if="page.content">
                    <div class="col-lg-10 mx-auto">
                        <div class="page-content-wrapper">
                            <div v-html="page.content" class="content-text" ref="pageContent"></div>
                        </div>
                    </div>
                </div>
                
                <!-- Page Items as Swiper Content Slider -->
                <div class="row mb-55" v-if="page.page_items && page.page_items.length">
                    <div class="col-12">
                        <div class="page-items-slider position-relative">
                            <swiper
                                :slidesPerView="1"
                                :spaceBetween="24"
                                :navigation="{ nextEl: '.items-next', prevEl: '.items-prev' }"
                                :breakpoints="{
                                    576: { slidesPerView: 1.5 },
                                    768: { slidesPerView: 2 },
                                    992: { slidesPerView: 3 }
                                }"
                                :modules="swiperModules"
                                :autoplay="{ delay: 4000, disableOnInteraction: false }"
                                class="items-swiper pb-4"
                            >
                                <swiper-slide v-for="item in page.page_items" :key="item.id">
                                    <div class="page-item-card bg-white round-20 overflow-hidden h-100 shadow-sm">
                                        <div class="item-image" v-if="item.image">
                                            <img :src="item.image" :alt="item.title" class="w-100" />
                                        </div>
                                        <div class="item-content p-30">
                                            <h4 class="fs-20 fw-semibold mb-15">{{ item.title }}</h4>
                                            <div v-html="item.content" class="text-para"></div>
                                        </div>
                                    </div>
                                </swiper-slide>
                            </swiper>
                            <!-- Custom Nav Buttons -->
                            <button class="items-prev slider-nav-btn" aria-label="Previous">
                                <i class="ri-arrow-left-s-line"></i>
                            </button>
                            <button class="items-next slider-nav-btn" aria-label="Next">
                                <i class="ri-arrow-right-s-line"></i>
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- Page Contents Section (from Content model) -->
                <div class="row" v-if="page.contents && page.contents.length">
                    <div 
                        class="col-md-6 col-12 mb-30" 
                        v-for="content in page.contents" 
                        :key="content.id"
                    >
                        <div class="content-card bg-white round-20 overflow-hidden h-100 shadow-sm">
                            <div class="content-image" v-if="content.featured_image">
                                <img :src="content.featured_image" :alt="content.title" class="w-100" />
                            </div>
                            <div class="content-body">
                                <h4 class="fs-20 fw-semibold mb-15">{{ content.title }}</h4>
                                <div v-if="content.content" v-html="content.content" class="content-html"></div>
                                <p v-else-if="content.description" class="text-para mb-0">{{ content.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Loading State -->
        <div class="container ptb-115" v-if="loading">
            <div class="row">
                <div class="col-12 text-center">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3 text-para">Loading page content...</p>
                </div>
            </div>
        </div>
        
        <!-- Error State -->
        <div class="container ptb-115" v-if="error">
            <div class="row">
                <div class="col-lg-8 mx-auto text-center">
                    <h2 class="section-title style-one fw-semibold mb-20">{{ error }}</h2>
                    <p class="text-para mb-30">The page you are looking for could not be found.</p>
                    <router-link to="/" class="btn style-one d-inline-flex flex-wrap align-items-center transition">
                        <span class="btn-text d-flex flex-column align-items-center justify-content-center transition">
                            Go to Homepage
                        </span>
                        <span class="btn-icon d-flex flex-column align-items-center justify-content-center transition">
                            <i class="ri-arrow-right-line"></i>
                        </span>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'

import PageBanner from '@/components/Common/PageBanner.vue'

export default {
    name: 'Pages',
    components: {
        PageBanner,
        Swiper,
        SwiperSlide
    },
    data() {
        return {
            page: null,
            loading: true,
            error: null,
            swiperModules: [Navigation, Autoplay]
        }
    },
    async mounted() {
        await this.loadPage()
    },
    watch: {
        '$route'() {
            this.loadPage()
        }
    },
    methods: {
        async loadPage() {
            const slug = this.$route.params.slug
            
            if (!slug) {
                this.error = 'Page not found'
                this.loading = false
                return
            }
            
            this.loading = true
            this.error = null
            
            try {
                const response = await this.$api.get(`public/pages/${slug}`)
                
                if (response.data && response.data.status === 'success') {
                    this.page = response.data.data
                    
                    // Update page meta
                    if (this.page.meta_title) {
                        document.title = this.page.meta_title
                    }
                    
                    if (this.page.meta_description) {
                        const metaDescription = document.querySelector('meta[name="description"]')
                        if (metaDescription) {
                            metaDescription.setAttribute('content', this.page.meta_description)
                        }
                    }

                    // Enhance galleries after DOM updates
                    this.$nextTick(() => {
                        this.enhanceGalleries()
                    })
                } else {
                    this.error = 'Page not found'
                }
            } catch (error) {
                console.error('Failed to load page:', error)
                if (error.response && error.response.status === 404) {
                    this.error = 'Page not found'
                } else {
                    this.error = 'Failed to load page content'
                }
            } finally {
                this.loading = false
            }
        },

        enhanceGalleries() {
            const lightbox = this.$root?.$refs?.galleryLightbox
            if (lightbox && this.$refs.pageContent) {
                lightbox.enhanceGalleries(this.$refs.pageContent)
            }
        }
    }
}
</script>

<style scoped>
/* ---- Page Item Slider Cards ---- */
.page-items-slider {
    padding: 0 50px;
}

.page-item-card {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.page-item-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
}

.page-item-card .item-image img {
    height: 220px;
    object-fit: contain;
    transition: transform 0.4s ease;
}

.page-item-card:hover .item-image img {
    transform: scale(1.05);
}

.page-item-card .item-image {
    overflow: hidden;
}

/* Slider nav buttons */
.slider-nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px solid var(--primaryColor, #303E4D);
    background: #fff;
    color: var(--primaryColor, #303E4D);
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.slider-nav-btn:hover {
    background: var(--primaryColor, #303E4D);
    color: #fff;
}

.slider-nav-btn.items-prev {
    left: 0;
}

.slider-nav-btn.items-next {
    right: 0;
}

.slider-nav-btn.swiper-button-disabled {
    opacity: 0.35;
    cursor: default;
}

/* ---- Content Cards (responsive grid) ---- */
.content-card {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.content-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.content-card .content-image {
    overflow: hidden;
}

.content-card .content-image img {
    height: 220px;
    object-fit: contain;
    transition: transform 0.4s ease;
}

.content-card:hover .content-image img {
    transform: scale(1.05);
}

.content-card .content-body {
    padding: 24px 28px 28px;
}

.content-html {
    font-size: 15px;
    line-height: 1.75;
    color: #555;
}

.content-html p {
    margin-bottom: 14px;
}

.content-html p:last-child {
    margin-bottom: 0;
}

.content-html img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 12px 0;
}

.content-html ul,
.content-html ol {
    margin-bottom: 14px;
    padding-left: 24px;
}

.content-html h1,
.content-html h2,
.content-html h3,
.content-html h4,
.content-html h5,
.content-html h6 {
    color: #333;
    font-weight: 600;
    margin-bottom: 12px;
    margin-top: 18px;
}

/* ---- Content text ---- */
.content-text {
    font-size: 16px;
    line-height: 1.8;
    color: #666;
}

.content-text h1,
.content-text h2,
.content-text h3,
.content-text h4,
.content-text h5,
.content-text h6 {
    color: #333;
    font-weight: 600;
    margin-bottom: 20px;
    margin-top: 30px;
}

.content-text p {
    margin-bottom: 20px;
}

.content-text ul,
.content-text ol {
    margin-bottom: 20px;
    padding-left: 30px;
}

.content-text img {
    max-width: 100%;
    height: auto;
    border-radius: 10px;
    margin: 20px 0;
}

/* Responsive */
@media (max-width: 767px) {
    .page-items-slider {
        padding: 0 40px;
    }
    
    .slider-nav-btn {
        width: 36px;
        height: 36px;
        font-size: 16px;
    }
}
</style>
